/**
 * Drawer State Management Hook
 * 
 * Manages the drawer stack state and provides methods for
 * opening, closing, and checking drawer states.
 */

import * as React from "react";
import { useDrawersStack } from "@mantine/core";
import { useStackActions } from "../../core/useStackActions";
import type { GlobalDrawerId } from "../types";
import { GLOBAL_DRAWER_IDS } from "../types";

/**
 * Return type for useDrawerState hook
 */
export interface UseDrawerStateReturn {
  drawerStack: ReturnType<typeof useDrawersStack<string>>;
  openDrawer: (id: GlobalDrawerId) => void;
  closeDrawer: (id: GlobalDrawerId) => void;
  closeAllDrawers: () => void;
  isDrawerOpen: (id: GlobalDrawerId) => boolean;
}

/**
 * Hook for managing drawer stack state
 * 
 * @returns Object with drawer state management methods
 */
export function useDrawerState(): UseDrawerStateReturn {
  let drawerStack;
  try {
    drawerStack = useDrawersStack(GLOBAL_DRAWER_IDS);
  } catch (error) {
    console.error('useDrawerState: Failed to initialize drawer stack. Ensure GlobalDrawerStackProvider wraps this component.', error);
    // Return a dummy stack that won't crash
    drawerStack = {
      register: () => ({ opened: false, onClose: () => {} }),
      open: () => {},
      close: () => {},
      closeAll: () => {},
      state: {},
    } as any;
  }
  
  const stackActions = useStackActions(drawerStack);

  const openDrawer = React.useCallback(
    (id: GlobalDrawerId) => {
      stackActions.open(id);
    },
    [stackActions]
  );

  const closeDrawer = React.useCallback(
    (id: GlobalDrawerId) => {
      stackActions.close(id);
    },
    [stackActions]
  );

  const closeAllDrawers = React.useCallback(() => {
    stackActions.closeAll();
  }, [stackActions]);

  const isDrawerOpen = React.useCallback(
    (id: GlobalDrawerId) => {
      return stackActions.isOpen(id);
    },
    [stackActions]
  );

  return React.useMemo(
    () => ({
      drawerStack,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      isDrawerOpen,
    }),
    [drawerStack, openDrawer, closeDrawer, closeAllDrawers, isDrawerOpen]
  );
}
